import { LegacyOrganizationAccount } from "../api/types/Account";
import { TLegacyUserWithAccount } from "../api/types/User";
import { localStorageKeys, useLocalStorage } from "../utils/localStorageItems";

export const ACL_NAMES = {
  // admins
  ACL_ADMIN_ACCOUNT: "acl.admin.account",
  ACL_ADMIN_ALERTPANEL: "acl.admin.devicealerts", // Alert Panel "acl.admin.alertpanel",
  ACL_ADMIN_ASSET_DEVICE: "acl.admin.device",
  ACL_ADMIN_CAMERA: "acl.admin.cameramedia", // controls display of "Cameras" admin menu section for Surfsight or Visionmax (Smart Witness Portal)
  ACL_ADMIN_CAMERA_CONFIG: "acl.camera.configuration", // controls display of "Configuration" and "Management" admin section under VisionMax camera admin
  ACL_ADMIN_CAMERA_INCOMING_CALL: "acl.admin.incomingcall", // controls incoming calls via VisionMax camera
  ACL_ADMIN_CAMERA_OUTGOING_CALL: "acl.admin.outgoingcall", // controls outgoing calls via VisionMax camera
  ACL_ADMIN_COMPLIANCE: "acl.fleethos.url",
  ACL_ADMIN_DRIVER: "acl.admin.driver",
  ACL_ADMIN_FLEETTAG: "acl.admin.entity.rfid",
  ACL_ADMIN_GEOZONE: "acl.admin.zone",
  ACL_ADMIN_DOCUMENT: "acl.admin.documents",
  ACL_ADMIN_IMPORT_EXPORT: "acl.admin.importexport",
  // ACL_ADMIN_GEOZONE_CORRIDOR: "acl.admin.corridor", // deprecated, using ACL_ADMIN_GEOZONE across app
  ACL_ADMIN_GROUP: "acl.admin.group",
  // ACL_ADMIN_HELPMENU: "acl.admin.helpmenu", // disabled 12/31/2024, help screen is now available to all users
  // ACL_ADMIN_MAINTENANCE: "acl.admin.devicemaint",//disabled 1/2/2025
  ACL_ADMIN_ROLE: "acl.admin.role",
  ACL_ADMIN_RULE: "acl.admin.rule",
  ACL_ADMIN_USER: "acl.admin.user",
  ACL_ADMIN_USER_ALL_USERS: "acl.admin.user:all",
  ACL_ADMIN_USER_GROUPS: "acl.admin.user:groups",
  ACL_ADMIN_USER_ROLE: "acl.admin.user:role",
  ACL_ADMIN_ROLE_ASSIGNTOUSER: "acl.admin.role:assigntouser",
  // ACL_ADMIN_ACTIVE_INACTIVE: "acl.admin.activeinactive",//disabled 1/2/2025

  // maps
  // ACL_MAP_ASSET: "acl.map.device", // Device Map Display //disabled 1/2/2025
  ACL_MAP_FLEET: "acl.map.fleet", // Group Map
  // seeded backend-side by acl/migrations/0007_seed_multivehicle_acl.py,
  // description "Multi-Vehicle Map", access levels 0 and 1 with 0 the
  // default - so the map is off until an account is granted it
  ACL_MAP_MULTI_VEHICLE: "acl.map.multivehicle",

  // user account / profile
  // ACL_PROFILE_CHANGE_PASSWORD: "acl.admin.password",//disabled 1/2/2025

  // messaging
  // ACL_PROFILE_MESSAGING: "acl.message.center", // disabled 12/31/2024, chat/messaging screen is now available to all users
  ACL_MESSAGING_SEND_MSG: "acl.message.info", // "acl.admin.driver:sendmassmsgok",
  ACL_MESSAGING_SEND_MASSMSG: "acl.admin.driver:sendmassmsgok",

  // superadmin
  ACL_SYSADMIN_ACCOUNTS: "acl.admin.sysadminaccounts",

  // log in as another user in the same account
  ACL_USER_LOGIN_AS: "acl.admin.userloginas",

  // device / asset
  ACL_ASSET_EDIT_CAMERA: "acl.admin.device:editcameraid",
  // ACL_MAP_VIDEOCAM: "acl.map.videocam",//disabled 1/2/2025
  ACL_ASSET_UNIQUE_ID: "acl.admin.device:uniqueid",
  ACL_ASSET_ACTIVE: "acl.admin.device:active",
  ACL_ASSET_EQUIP_STATUS: "acl.admin.device:editequipstat",
  ACL_ASSET_FUEL_CAPACITY: "acl.admin.device:fuelcapacity",
  ACL_ASSET_FUEL_ECONOMY: "acl.admin.device:fueleconomy",
  // ACL_ASSET_SERVER_ID: "acl.admin.device:serverid",//disabled 1/2/2025
  // ACL_ASSET_FIRMWARE: "acl.admin.device:firmware",//disabled 1/2/2025
  ACL_ASSET_CLR_ODOMETER_OFFSET: "acl.admin.device:clearodomoffset",
  ACL_ASSET_CLR_ENG_HOURS_OFFSET: "acl.admin.device:clearenghrsoffset",
  ACL_ASSET_VIN_DECODE: "acl.admin.device:vindecode",
  // driver
  ACL_ADMIN_DRIVER_LICENSE: "acl.admin.driver.license",
  ACL_ADMIN_DRIVER_ACTIVE: "acl.admin.driver.active",
  ACL_ADMIN_DRIVER_BADGE: "acl.admin.driver:badge",
  ACL_ADMIN_DRIVER_CARD: "acl.admin.driver:card",

  // event colors (FT1 StatusCodeInfo)
  ACL_ADMIN_STATUS_CODE: "acl.admin.statusCode",

  // heatmap
  ACL_ADMIN_HEATMAP_CONFIG_ADMIN: "acl.admin.heatmap",
  ACL_ADMIN_HEATMAP: "acl.map.heat",

  // crash track
  ACL_ADMIN_CRASHTRACK: "acl.map.crashtrack",

  // White labeling
  ACL_SYSADMIN_WHITE_LABEL: "acl.admin.sysadminlaf",

  // Manager Access
  ACL_ADMIN_MANAGER_LOOKUP: "acl.admin.managerlookup",
  ACL_INCIDENT_REVIEW: "acl.admin.incidentreview",
  ACL_INCIDENT_CONFIG: "acl.admin.incidentreviewconfig",
  ACL_INCAB_COACHING: "acl.admin.incabcoachingconfig",
  ACL_ZIPTIE_CONFIG: "acl.admin.serializedziptie",
};

export const useACL = () => {
  const { getLocalStorageItem } = useLocalStorage();

  const getUserACLAccessLevel = (
    aclName: (typeof ACL_NAMES)[keyof typeof ACL_NAMES]
  ) => {
    const thisUser = {
      ownerOrganization: getLocalStorageItem(
        localStorageKeys.ownerOrganization
      ) as LegacyOrganizationAccount,
      user: getLocalStorageItem(
        localStorageKeys.user
      ) as TLegacyUserWithAccount,
    };
    return (
      ((thisUser.user || {}) as TLegacyUserWithAccount)?.acls?.find(
        (acl) => acl.name === aclName
      )?.access_level || 0
    );
  };

  return { getUserACLAccessLevel };
};
